import { DownloadSimpleIcon, EyeIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useDownloadReservationsCSV } from '@/api/reservations/download-reservations-csv'
import { useGetAmenities } from '@/api/reservations/get-amenities'
import {
  ReservationDashboardRow,
  ReservationSortBy,
  USE_GET_RESERVATION_DASHBOARD_QUERY_KEY,
  useGetReservationDashboard,
  useInvalidateReservationDashboard,
} from '@/api/reservations/get-reservation-dashboard'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { DEFAULT_TZ } from '@/constants/date'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { DateRange } from '@/models/helpers'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import {
  selectIsDashboardToggled,
  setRequestStatus,
  TOGGLE_DASHBOARD,
} from '@/reducers/Organisation/reservationsReducer'
import { buildCsvFilename, downloadCsv } from '@/utils/file'

import {
  getDayEndEpoch,
  getDayStartEpoch,
  toBuildingDay,
  toBuildingMidnight,
} from '../reservations-dates.utils'
import { DeclineReservationConfirmationPopup } from './decline-reservation-confirmation-popup'
import {
  buildAmenitySpaceTreeOptions,
  getAmenitySpacePlaceholder,
  getAmenitySpaceSearchPlaceholder,
  getDefaultRange,
  getReservationStatusTabOptions,
  getReservationsPeriodOptions,
  getReservationsTableColumns,
  NON_BLOCKED_STATUSES,
  RESERVATIONS_TABLE_ID,
  ReservationStatusFilter,
  reservationsTableFiltersSchema,
  reservationsTableInitialFilters,
  splitAmenitySpaceSelection,
  toAmenitySpaceCounts,
  withDateRangeReset,
} from './reservations-dashboard.constants'
import * as S from './reservations-dashboard.styled'
import type { ReservationsDashboardTabProps } from './reservations-dashboard.types'

export const ReservationsDashboardTab = ({
  resident,
  building,
}: ReservationsDashboardTabProps = {}) => {
  const { t } = useTranslation(['reservations'])
  const dispatch = useDispatch() as any
  const { navigate } = routerFactory.useRouter()
  const activeBuilding = useSelector(selectActiveBuilding)
  const isDashboardToggled = useSelector(selectIsDashboardToggled)
  const invalidateReservations = useInvalidateReservationDashboard()

  const selectedBuilding = building ?? activeBuilding
  const buildingId = selectedBuilding?.id
  const timezone = selectedBuilding?.timezone ?? DEFAULT_TZ
  const clientIds = resident ? [resident.id] : undefined

  useEffect(() => {
    if (isDashboardToggled) {
      invalidateReservations()
      dispatch({ toggle: false, type: TOGGLE_DASHBOARD })
    }
  }, [isDashboardToggled])

  const searchParams = useSearchParams({
    initialState: reservationsTableInitialFilters,
    schema: reservationsTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: reservationsTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const rawSearch = tableFilters.getFilter('search')
  const search = rawSearch?.trim()
  const [searchDraft, handleSearchChange] = useSearchDraft(rawSearch, (value) =>
    tableFilters.setFilter('search', value),
  )

  const statusFilter = tableFilters.getFilter('status') as ReservationStatusFilter
  const fromDateFilter = tableFilters.getFilter('fromDate')
  const toDateFilter = tableFilters.getFilter('toDate')
  const amenitiesAndSpacesFilter = tableFilters.getFilter('amenitiesAndSpaces') ?? []
  const sortBy = tableFilters.getFilter('sortBy')
  const sortOrder = tableFilters.getFilter('sortOrder')
  const skip = tableFilters.getFilter('skip') ?? 0
  const limit = tableFilters.getFilter('limit') ?? reservationsTableInitialFilters.limit ?? 25

  const allDates = !!tableFilters.getFilter('allDates')
  const [defaultFrom, defaultTo] = useMemo(() => getDefaultRange(timezone), [timezone])
  const effectiveFromDate = allDates ? undefined : (fromDateFilter ?? defaultFrom)
  const effectiveToDate = allDates ? undefined : (toDateFilter ?? defaultTo)
  const dateRange: DateRange = [
    effectiveFromDate ? toBuildingDay(effectiveFromDate, timezone) : null,
    effectiveToDate ? toBuildingDay(effectiveToDate, timezone) : null,
  ]

  const statusParam =
    !statusFilter || statusFilter === 'All' ? NON_BLOCKED_STATUSES : [statusFilter]

  const { amenityIds, locationIds } = useMemo(
    () => splitAmenitySpaceSelection(amenitiesAndSpacesFilter),
    [amenitiesAndSpacesFilter],
  )

  const { data, isFetching, error } = useGetReservationDashboard({
    amenityId: amenityIds.length ? amenityIds : undefined,
    buildingId,
    clientId: clientIds,
    dateEnd: effectiveToDate,
    dateStart: effectiveFromDate,
    limit,
    locationId: locationIds.length ? locationIds : undefined,
    search: search || undefined,
    skip,
    sortBy: sortBy as ReservationSortBy | undefined,
    sortOrder,
    status: statusParam,
  })

  useEffect(() => {
    if (error?.response?.status === 403) {
      toastFactory.error(t('reservations:dashboard.toasts.noPermissionError'))
    }
  }, [error])

  const { data: amenities = [] } = useGetAmenities(buildingId)

  const amenitySpaceCounts = useMemo(
    () => toAmenitySpaceCounts(data?.filterCounts),
    [data?.filterCounts],
  )

  // Note: the default date range isn't a tracked filter, so an empty default view can't tell
  // "no reservations at all" from "none in this range" - probe all dates (only when empty).
  const isViewEmpty = !!data && !isFetching && data.results.length === 0
  const { data: anyReservationsData } = useGetReservationDashboard({
    buildingId: isViewEmpty ? buildingId : undefined,
    clientId: clientIds,
    limit: 1,
    skip: 0,
    status: NON_BLOCKED_STATUSES,
  })
  const hasReservationsOutsideView = isViewEmpty && (anyReservationsData?.count ?? 0) > 0

  const amenitySpaceTreeOptions = useMemo(
    () => buildAmenitySpaceTreeOptions(amenities, amenitySpaceCounts),
    [amenities, amenitySpaceCounts],
  )

  const allCount =
    (data?.countPending ?? 0) +
    (data?.countApproved ?? 0) +
    (data?.countCancelled ?? 0) +
    (data?.countCompleted ?? 0) +
    (data?.countOngoing ?? 0)

  const statusCounts = {
    approved: data?.countApproved ?? 0,
    cancelled: data?.countCancelled ?? 0,
    completed: data?.countCompleted ?? 0,
    count: allCount,
    ongoing: data?.countOngoing ?? 0,
    pending: data?.countPending ?? 0,
  }

  const statusTabOptions = useMemo(() => getReservationStatusTabOptions(t, statusCounts), [data, t])

  const paginationTotal =
    !statusFilter || statusFilter === 'All'
      ? allCount
      : ((statusCounts as Record<string, number>)[statusFilter] ?? 0)

  const [approvingIds, setApprovingIds] = useState(new Set<number>())

  const onApproveReservation = async (reservation: ReservationDashboardRow) => {
    setApprovingIds((prev) => new Set(prev).add(reservation.id))
    try {
      await dispatch(setRequestStatus([reservation.id], 'approve'))
      invalidateReservations()
      toastFactory.success(t('reservations:dashboard.toasts.approveSuccess'))
    } catch {
      toastFactory.error(t('reservations:dashboard.toasts.approveError'))
    } finally {
      setApprovingIds((prev) => {
        const next = new Set(prev)
        next.delete(reservation.id)
        return next
      })
    }
  }

  const onDeclineReservation = (reservation: ReservationDashboardRow) =>
    popupFactory.open(DeclineReservationConfirmationPopup, { reservation })

  const reservedForColumnSettings = usePersonColumnSettings({
    column: 'reservedFor',
    dashboard: 'Reservations',
    fieldType: 'mixed',
    rowsQueryKey: [USE_GET_RESERVATION_DASHBOARD_QUERY_KEY],
  })
  const createdByColumnSettings = usePersonColumnSettings({
    column: 'createdBy',
    dashboard: 'Reservations',
    fieldType: 'mixed',
    rowsQueryKey: [USE_GET_RESERVATION_DASHBOARD_QUERY_KEY],
  })
  const approvedByColumnSettings = usePersonColumnSettings({
    column: 'approvedBy',
    dashboard: 'Reservations',
    fieldType: 'staff',
    rowsQueryKey: [USE_GET_RESERVATION_DASHBOARD_QUERY_KEY],
  })

  const columns = getReservationsTableColumns(
    t,
    timezone,
    {
      approvingIds,
      onApprove: onApproveReservation,
      onDecline: onDeclineReservation,
    },
    { approvedByColumnSettings, createdByColumnSettings, reservedForColumnSettings },
  )

  const { mutateAsync: downloadReservationsCSV, isPending: isCsvDownloadPending } =
    useDownloadReservationsCSV()

  // Note: the /reservation/report endpoint (unlike /reservation/dashboard) requires
  // startDate/endDate to be exact midnight in the building's timezone - each bound's
  // calendar day is reduced to that day's midnight (dropping the end-of-day time the main
  // dashboard query needs for its own inclusive filtering but this endpoint doesn't want).

  const handleDownloadCsv = async () => {
    if (!buildingId) return

    try {
      const blob = await downloadReservationsCSV({
        amenityId: amenityIds.length ? amenityIds : undefined,
        buildingId,
        endDate: effectiveToDate ? toBuildingMidnight(effectiveToDate, timezone) : undefined,
        locationId: locationIds.length ? locationIds : undefined,
        search: search || undefined,
        startDate: effectiveFromDate ? toBuildingMidnight(effectiveFromDate, timezone) : undefined,
        status: statusParam,
      })
      downloadCsv({
        blob,
        filename: buildCsvFilename({
          building: selectedBuilding?.name,
          context: allDates ? ['All dates'] : undefined,
          dateRange:
            effectiveFromDate && effectiveToDate
              ? [new Date(effectiveFromDate), new Date(effectiveToDate)]
              : undefined,
          page: 'reservations',
          timezone,
        }),
      })
    } catch {
      toastFactory.error(t('reservations:dashboard.toasts.exportError'))
    }
  }

  // Note: Details is the only row action left - clicking the row opens it (primary), so no
  // dropdown/kebab is rendered (showRowActions requires at least one non-primary action).
  const rowActions: RowAction<ReservationDashboardRow>[] = [
    {
      icon: EyeIcon,
      label: t('reservations:dashboard.actions.details'),
      onClick: (row) =>
        navigate({
          params: { reservationId: String(row.id) },
          path: '/reservations/:reservationId',
        }),
      primary: true,
    },
  ]

  const handleSort = (nextSortBy: string | null, nextSortOrder: SortOrder | null) => {
    tableFilters.setFilters({
      sortBy: nextSortBy ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  return (
    <TableGroup
      id={RESERVATIONS_TABLE_ID}
      columns={columns}
      data={data?.results ?? []}
      emptyNoFilters={{
        description: t('reservations:dashboard.empty.noFilters.description'),
        title: t('reservations:dashboard.empty.noFilters.title'),
      }}
      emptyWithFilters={{
        description: t('reservations:dashboard.empty.withFilters.description'),
        title: t('reservations:dashboard.empty.withFilters.title'),
      }}
      forceFilteredEmptyState={hasReservationsOutsideView}
      filters={
        <S.AmenitySpaceFilterWrapper>
          <Select
            checkbox
            searchable
            type="nested"
            multipleSelectedLabel={t(
              'reservations:shared.amenitySpaceFilter.multipleSelectedLabel',
            )}
            options={amenitySpaceTreeOptions}
            placeholder={getAmenitySpacePlaceholder(t)}
            searchPlaceholder={getAmenitySpaceSearchPlaceholder(t, amenitiesAndSpacesFilter.length)}
            size="small"
            value={amenitiesAndSpacesFilter}
            onChange={(value) => tableFilters.setFilter('amenitiesAndSpaces', value)}
            onReset={() => tableFilters.setFilter('amenitiesAndSpaces', [])}
          />
        </S.AmenitySpaceFilterWrapper>
      }
      getRowKey={(row) => row.id}
      loading={isFetching}
      sortBy={sortBy ?? null}
      sortOrder={sortOrder ?? null}
      navigation={{
        // Note: the CSV report endpoint can't be scoped to one resident, so it's hidden when embedded.
        actions: resident ? undefined : (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('reservations:dashboard.actions.exportCsv')}
            disabled={!buildingId}
            loading={isCsvDownloadPending}
            onClick={handleDownloadCsv}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        datePicker: (
          <DatePickerInput
            mode="range"
            periodOptions={getReservationsPeriodOptions(t, timezone)}
            placeholder={t('reservations:shared.datePicker.placeholder')}
            size="small"
            value={dateRange}
            onRangeChange={(range) => {
              const fromDate = range[0] ? getDayStartEpoch(range[0], timezone) : undefined
              const toDate = range[1] ? getDayEndEpoch(range[1], timezone) : undefined
              tableFilters.setFilters({ allDates: !range[0] && !range[1], fromDate, toDate })
            }}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            name="search"
            placeholder={t('reservations:dashboard.search.placeholder')}
            size="small"
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        ),
        statusTabs: (
          <StatusTabs
            options={statusTabOptions}
            value={statusFilter ?? null}
            onChange={(value) => tableFilters.setFilter('status', value)}
          />
        ),
      }}
      pagination={{
        paginationFilters: tableFilters,
        total: paginationTotal,
      }}
      rowActions={rowActions}
      tableFilters={withDateRangeReset(tableFilters)}
      onSort={handleSort}
    />
  )
}
